import React from 'react';
import * as S from './components/LogTable.styled';
import UsernamePhotoWithPreview from './components/UsernamePhotoWithPreview';
import Room from '@mui/icons-material/MeetingRoom';
import Branches from '@mui/icons-material/AccountTree';
import Flight from '@mui/icons-material/Flight';
import Hotel from '@mui/icons-material/Hotel';
import Home from '@mui/icons-material/Home';
import Apartment from '@mui/icons-material/Apartment';
import Business from '@mui/icons-material/Business';
import ArrowForward from '@mui/icons-material/ArrowForward';
import Inventory2 from '@mui/icons-material/Inventory2';
import Person from '@mui/icons-material/Person';
import { LOG_MESSAGE_SPLIT_RE } from './logUtils';
import {
  REMOVED_ACTOR_LABEL,
  resolveActorDisplayName,
  resolveActorUser,
  StaffMaps,
} from './logActor';
import {
  CustomerMaps,
  REMOVED_CUSTOMER_LABEL,
  resolveCustomer,
  resolveCustomerDisplayName,
} from './logCustomer';
import type { Log } from './hooks';

/** Channel-manager OTA keys → display label + brand-tinted chip + icon. */
const otaMeta: Record<string, {
  label: string;
  accent: string;
  darkText?: boolean;
  Icon: typeof Flight;
}> = {
  BookingCom: { label: 'Booking.com', accent: '#003580', Icon: Hotel },
  Expedia: { label: 'Expedia', accent: '#FFC72C', darkText: true, Icon: Flight },
  Airbnb: { label: 'Airbnb', accent: '#FF5A5F', Icon: Home },
  Agoda: { label: 'Agoda', accent: '#E21E26', Icon: Apartment },
  HotelsCom: { label: 'Hotels.com', accent: '#D32F2F', Icon: Business },
};

export type FormatAuditMessageOptions = {
  currency: string;
  staffMaps: StaffMaps;
  customerMaps: CustomerMaps;
  userPhotoByName?: Map<string, string>;
  customerPhotoByToken?: Map<string, string>;
  /** Value column: show unit counts instead of money. */
  unitsMode?: boolean;
  /** Parent row — used for deleted-audit preview on &L& links. */
  parentLog?: Log;
  canPreviewDeletedAudit?: boolean;
  // eslint-disable-next-line no-unused-vars
  onAuditIdClick?: (targetId: string, kind: 'audit' | 'restored') => void;
  // eslint-disable-next-line no-unused-vars
  onPreviewDeletedAudit?: (targetId: string) => void;
};

/**
 * Same message/value token rendering as /audits details.
 * Shared by LogTable and Reporting preview.
 */
export const formatAuditMessageEntry = (
  entry: string,
  opts: FormatAuditMessageOptions,
): React.ReactNode[] => {
  const {
    currency,
    staffMaps,
    customerMaps,
    userPhotoByName = new Map(),
    customerPhotoByToken = new Map(),
    unitsMode,
    parentLog,
    canPreviewDeletedAudit,
    onAuditIdClick,
    onPreviewDeletedAudit,
  } = opts;

  const numberEntry = Number(entry);
  if (numberEntry || numberEntry === 0 || entry === 'N/A') {
    if (entry === 'N/A') {
      return [<span key="na">N/A</span>];
    }
    if (unitsMode) {
      return [
        <S.UnitsEntry key="units">{numberEntry.toLocaleString()}</S.UnitsEntry>,
      ];
    }
    if (numberEntry === 0) {
      return [<span key="zero">0</span>];
    }
    if (numberEntry > 0) {
      return [
        <S.CashIn key="cashin">{`${currency}${numberEntry.toLocaleString()}`}</S.CashIn>,
      ];
    }
    return [
      <S.CashOut key="cashout">{`${currency}${numberEntry.toLocaleString()}`}</S.CashOut>,
    ];
  }

  const normalizeStockValueToken = (raw: string): string => String(raw || '').replace(/^&|&$/g, '').trim();
  const trailingStockMatch = entry.match(/^(.*?)(?:\s*·\s*stock\s*&A&→&A&\s*(&[^&]+&)\s*to\s*(&[^&]+&))\s*$/);
  if (trailingStockMatch) {
    const [, prefix, startToken, leftToken] = trailingStockMatch;
    const prefixNodes = formatAuditMessageEntry(prefix, opts);
    const startText = normalizeStockValueToken(startToken);
    const leftText = normalizeStockValueToken(leftToken);
    return [
      ...prefixNodes,
      <S.StockChange key="stock-change">
        <S.StockChangeLine>
          <S.StockChangeIcon aria-hidden>
            <Inventory2 fontSize="small" sx={{ fontSize: 13 }} />
          </S.StockChangeIcon>
          <S.StockChangeLabel>stock</S.StockChangeLabel>
          <span>
            {' '}
            reduced from
            {' '}
            {startText}
            {' '}
            to
          </span>
          <S.StockChangeValue>{leftText}</S.StockChangeValue>
        </S.StockChangeLine>
      </S.StockChange>,
    ];
  }

  // &L& / &R& / &G& must appear before bare & so audit links / customers stay tokens.
  // Do not split on `_` / `#` — customer ids like `c_chi` must stay one token.
  const splitWords: any[] = entry.split(LOG_MESSAGE_SPLIT_RE);

  // Map each split slot to its character range so identical tokens (e.g. &1& vs |1|)
  // are not confused by indexOf.
  const splitWordRanges: { start: number, end: number, index: number }[] = [];
  {
    let cursor = 0;
    splitWords.forEach((word, index) => {
      if (typeof word !== 'string' || word === '') {
        splitWordRanges.push({ start: cursor, end: cursor, index });
        return;
      }
      const at = entry.indexOf(word, cursor);
      if (at < 0) {
        splitWordRanges.push({ start: cursor, end: cursor, index });
        return;
      }
      splitWordRanges.push({ start: at, end: at + word.length, index });
      cursor = at + word.length;
    });
  }

  const seperators = {
    '|': (text: string) => {
      const user = resolveActorUser(text, staffMaps);
      const isRemoved = !user?.username;
      const displayName = isRemoved
        ? REMOVED_ACTOR_LABEL
        : resolveActorDisplayName(text, staffMaps, text);
      const photoSrc = isRemoved
        ? undefined
        : (userPhotoByName.get(String(text))
          || userPhotoByName.get(displayName.replace(/\s+/g, '').toLowerCase()));
      return (
        <S.Username isRemoved={isRemoved}>
          <UsernamePhotoWithPreview
            src={photoSrc}
            fallback={(
              <S.UsernamePhotoFallback aria-hidden isRemoved={isRemoved}>
                <Person />
              </S.UsernamePhotoFallback>
            )}
          />
          <S.UsernameText isRemoved={isRemoved}>{displayName}</S.UsernameText>
        </S.Username>
      );
    },
    '&': (text: string) => (
      <S.Bold>
        {text}
      </S.Bold>
    ),
    // After bare `&` so `&G&…&G&` overwrites any overlapping bold match.
    '&G&': (text: string) => {
      const customer = resolveCustomer(text, customerMaps);
      const isRemoved = !customer?.name;
      const displayName = isRemoved
        ? REMOVED_CUSTOMER_LABEL
        : resolveCustomerDisplayName(text, customerMaps, text);
      const photoSrc = isRemoved
        ? undefined
        : (customerPhotoByToken.get(String(text))
          || customerPhotoByToken.get(displayName.replace(/\s+/g, '').toLowerCase()));
      return (
        <S.Customer isRemoved={isRemoved}>
          <UsernamePhotoWithPreview
            src={photoSrc}
            ring
            fallback={(
              <S.UsernamePhotoFallback aria-hidden isRemoved={isRemoved} $ring>
                <Person />
              </S.UsernamePhotoFallback>
            )}
          />
          <S.UsernameText isRemoved={isRemoved}>{displayName}</S.UsernameText>
        </S.Customer>
      );
    },
    '&V&': (text: string) => (
      <S.Room>
        <Room fontSize="small" sx={{ scale: '0.65' }} />
        <S.RoomText>{text}</S.RoomText>
      </S.Room>
    ),
    '!': (text: string) => (
      <S.External>
        <Branches fontSize="small" sx={{ scale: '0.65' }} />
        <S.ExternalText>{text}</S.ExternalText>
      </S.External>
    ),
    '^': (text: string) => (
      <span style={{ color: text.toLowerCase() === 'restored' ? S.cashColor : '#eb364b' }}>
        {text}
      </span>
    ),
    '&P&': (text: string) => (
      <S.Bold>
        {`${currency}${Number(text).toLocaleString()}`}
      </S.Bold>
    ),
    '&C&': (text: string) => {
      const resolved = otaMeta[text];
      const Icon = resolved?.Icon || Flight;
      return (
        <S.OTA
          $accent={resolved?.accent}
          $darkText={Boolean(resolved?.darkText)}
        >
          <Icon fontSize="small" sx={{ scale: '0.6' }} />
          {resolved?.label || text}
        </S.OTA>
      );
    },
    '&A&': () => (
      <S.StockArrow aria-hidden>
        <ArrowForward fontSize="small" sx={{ fontSize: 14 }} />
      </S.StockArrow>
    ),
    '&L&': (text: string) => {
      const targetId = String(text || '').trim();
      const canPreview = Boolean(
        parentLog
        && canPreviewDeletedAudit
        && parentLog.type === 'Audit deleted'
        && parentLog.logTable?.id,
      );
      return (
        <S.AuditIdLink
          type="button"
          title={canPreview ? 'Preview deleted audit' : 'Go to audit'}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!targetId) return;
            if (canPreview && onPreviewDeletedAudit) {
              onPreviewDeletedAudit(targetId);
              return;
            }
            onAuditIdClick?.(targetId, 'audit');
          }}
        >
          audit
        </S.AuditIdLink>
      );
    },
    // Post-restore deleted rows: clickable "restored" → Audit restored log.
    '&R&': (text: string) => {
      const targetId = String(text || '').trim();
      return (
        <S.RestoredIdLink
          type="button"
          title="Go to restore log"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!targetId) return;
            onAuditIdClick?.(targetId, 'restored');
          }}
        >
          restored
        </S.RestoredIdLink>
      );
    },
    '&-&': () => <></>,
  };

  Object.entries(seperators).forEach(([key, value]) => {
    const eArr = entry.split('');
    const inds: number[][] = [];
    let pos = 'start';
    let ind: number[] = [];

    eArr.forEach((e, i) => {
      const thisEntryToKey = `${eArr[i - 2]}${eArr[i - 1]}${e}`;
      const isSeperatorKey = thisEntryToKey === key;
      if ((e === key || isSeperatorKey) && pos === 'start') {
        ind = [];
        ind.push(i + 1);
        pos = 'end';
        return;
      }
      if ((e === key || isSeperatorKey) && pos === 'end') {
        if (e === key) {
          ind.push(i);
        } else {
          ind.push(i - 2);
        }
        inds.push(ind);
        pos = 'start';
      }
    });

    inds.forEach((i) => {
      const word = entry.substring(i[0], i[1]);
      const range = splitWordRanges.find((r) => (
        r.start === i[0]
        && r.end === i[1]
        && typeof splitWords[r.index] === 'string'
      )) || splitWordRanges.find((r) => (
        typeof splitWords[r.index] === 'string'
        && splitWords[r.index] === word
      ));
      if (!range) return;
      splitWords[range.index] = value(word);
    });
    splitWords.forEach((w, i) => {
      if (w === '%') {
        splitWords[i] = <div />;
      }
    });
  });
  return splitWords;
};
